import { any } from 'underscore';
import PropTypes from 'prop-types';
import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { Button, Link, mergeClasses, makeStyles } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { BidAdjustment as BidAdjustmentView } from '@bingads-webui-campaign/target-bid-adjustment';

import { getBidAdjustmentStyles } from './styles/bid-adjustment-styles';

const useStyles = makeStyles({
  hide: {
    display: 'none',
  },
});

const TargetEditButton = (props) => {
  const {
    isBidAdjustmentsVisible, onClick, isTargetSelected, i18n, classes, readOnly,
  } = props;

  if (readOnly || !isBidAdjustmentsVisible) {
    return null;
  }

  return (
    <div className={mergeClasses('edit-target-button-container', classes.editTargetButtonContainer)}>
      {
        !isTargetSelected && (
          <span className={mergeClasses('no-target-selected', classes.noTargetSelected)}>
            {i18n.getString(_TL_('No targets selected.'))}
            &nbsp;
          </span>
        )
      }
      {
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link inline onClick={() => onClick()}>{i18n.getString(_TL_('Edit target'))}</Link>
      }
    </div>
  );
};

TargetEditButton.propTypes = {
  isBidAdjustmentsVisible: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isTargetSelected: PropTypes.bool.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    editTargetButtonContainer: PropTypes.string,
    noTargetSelected: PropTypes.string,
  }).isRequired,
  readOnly: PropTypes.bool,
};

TargetEditButton.defaultProps = {
  readOnly: false,
};

// 'targetNameList' is the list of fields in validationProps.data that are used to construct target grid
export const withBidAdjustment = (Component, criterionType, targetsFieldNameList) => {
  const HOC = (props) => {
    const { showBidAdjustments, readOnly, ...rest } = props;
    const [isBidAdjustmentsVisible, setIsBidAdjustmentsVisible] = useState(showBidAdjustments);
    const [bidAdjustmentRef, setBidAdjustmentRef] = useState();
    const [targetInfo, setTargetInfo] = useState();

    const i18n = useContext(I18nContext);

    const bidAdjustmentclasses = useDefaultStyles(getBidAdjustmentStyles);
    const styles = useStyles();

    const { validationProps = {} } = props;
    const { data } = validationProps;

    const isTargetSelected = useMemo(() => data && targetsFieldNameList.length > 0 &&
      any(targetsFieldNameList, targetsFieldName => data[targetsFieldName] && data[targetsFieldName].length > 0), [data]);

    const updateTargetInfo = useCallback(info => setTargetInfo(info), []);

    useEffect(() => {
      if (bidAdjustmentRef && bidAdjustmentRef.childElementCount === 0 && isBidAdjustmentsVisible && targetInfo) {
        const bidAdjustmentView = new BidAdjustmentView({
          currentLevelTargetInfo: targetInfo,
          criterionTypes: [criterionType],
          isAudienceCampaign: true,
          isForVNext: true,
          supportCashbackSettings: false,
          readonly: readOnly,
        });

        bidAdjustmentView.render();

        bidAdjustmentRef.innerHTML = '';
        bidAdjustmentRef.append(bidAdjustmentView.el);
      }
    }, [bidAdjustmentRef, isBidAdjustmentsVisible, readOnly, targetInfo]);

    const updateBidAdjustmentVisibility = useCallback((isVisible) => {
      setIsBidAdjustmentsVisible(isVisible);
    }, []);

    const targetEditButtonProps = useMemo(() => ({
      readOnly,
      isBidAdjustmentsVisible,
      onClick: updateBidAdjustmentVisibility,
      isTargetSelected,
      i18n,
      classes: bidAdjustmentclasses,
    }), [readOnly, isBidAdjustmentsVisible, updateBidAdjustmentVisibility, isTargetSelected, i18n, bidAdjustmentclasses]);

    return (
      <div className="with-bid-adjustment-container">
        <TargetEditButton {...targetEditButtonProps} />
        <Component {...rest} onAudienceTargetInfoChange={updateTargetInfo} shouldHide={isBidAdjustmentsVisible} />
        <div className={mergeClasses(bidAdjustmentclasses.pickerContainer, (!isBidAdjustmentsVisible || !isTargetSelected) && styles.hide)}>
          <div className="criterion-setting-container">
            <div className="target-edit-container">
              <div ref={newRef => setBidAdjustmentRef(newRef)} />
            </div>
          </div>
        </div>
        {
          showBidAdjustments && !isBidAdjustmentsVisible && (
            <Button appearance="primary" onClick={() => setIsBidAdjustmentsVisible(true)} className="done-button">
              {i18n.getString(_TL_('Done'))}
            </Button>
          )
        }
      </div>
    );
  };

  HOC.propTypes = {
    showBidAdjustments: PropTypes.bool,
    readOnly: PropTypes.bool,
    validationProps: PropTypes.shape({
      data: PropTypes.shape({}),
    }).isRequired,
  };

  HOC.defaultProps = {
    showBidAdjustments: false,
    readOnly: false,
  };

  return HOC;
};
